/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  getPriceChange.js
 */

const YEAR_IN_SECONDS = 31536000

interface PriceDatum {
  x: number
  y: number
}

export const getPriceChange = (
  series?: readonly PriceDatum[],
  timeStamp = Math.floor(Date.now() / 1000),
  delta = YEAR_IN_SECONDS
) => {
  if (!series?.length) {
    return null
  }

  const firstTime = series[0]?.x
  const timeAgo = timeStamp - delta
  if (firstTime > timeAgo) {
    // we don't have enough data for the requested time period.
    return null
  }

  const endPoint = series[series.length - 1]
  const endPrice = endPoint?.y

  for (let i = series.length - 1; i >= 0; i--) {
    const current = series[i]
    const { x: time, y: price } = current
    if (i === 0 || time <= timeAgo) {
      // returning tuple of the starting price from timeAgo, and most recently recorded price
      return [price, endPrice]
    }
  }
}

export const getPercentage = (p1?: number | null, p2?: number | null) => {
  if (!p1 || p2 === undefined || p2 === null) {
    return null
  }
  return ((p2 - p1) / p1) * 100
}

export const getPriceChangePercentage = (
  p1?: number | null,
  p2?: number | null,
  fixed = 0
) => {
  const percentage = getPercentage(p1, p2)
  if (percentage === null) {
    return null
  }
  const fixedPercentage = percentage.toFixed(fixed)
  return Number(fixedPercentage) === 0
    ? fixedPercentage.replace('-', '')
    : fixedPercentage
}

export const getPriceChangeString = (
  percentage?: string | null,
  downWord = 'decrease',
  upWord = 'increase'
) => {
  if (!percentage) {
    return null
  }
  if (Number(percentage) === 0) {
    return '0'
  }
  return percentage[0] === '-'
    ? `${percentage.replace('-', '')}% ${downWord}`
    : `${percentage.replace('-', '')}% ${upWord}`
}
